import React, { useState } from 'react';
import StarRating from './StarRating';
import { useAuth } from '../../context/AuthContext';
import { replyToReview } from '../../services/reviewService';
import { MessageCircle, Send, Loader2, Edit2 } from 'lucide-react';
import DOMPurify from 'dompurify';

const ReviewCard = ({ review }) => {
  const { userName, userPhoto, rating, reviewText, updatedAt, createdAt } = review;

  // Format the date based on Lobab standard
  const dateObj = updatedAt?.toDate() || createdAt?.toDate() || new Date();
  const dateStr = dateObj.toLocaleDateString('ar-EG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const { currentUser, userData, isAdmin, isInstructor } = useAuth();
  const [replyText, setReplyText] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const canReply = isAdmin || (isInstructor && review.targetType === 'instructor' && review.targetId === currentUser?.uid);

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setIsReplying(true);
    try {
      await replyToReview(review.id, replyText, {
        name: userData?.name,
        photoURL: userData?.photoURL
      });
      // Optionally reload or update local state
      review.reply = {
        text: replyText,
        instructorName: userData?.name,
        instructorPhoto: userData?.photoURL,
        repliedAt: { toDate: () => new Date() } // Mock for immediate update
      };
      setShowReplyInput(false);
      setIsEditing(false);
      setReplyText('');
    } catch (err) {
      console.error(err);
      alert('حدث خطأ أثناء إرسال الرد');
    } finally {
      setIsReplying(false);
    }
  };

  return (
    <div className="flex-shrink-0 w-80 max-w-full p-6 space-y-4 rounded-2xl bg-white dark:bg-white/5 dark:backdrop-blur-xl border border-gray-100 dark:border-white/10 shadow-sm dark:shadow-[0_0_15px_rgba(218,165,32,0.05)] transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-md dark:hover:shadow-[0_0_20px_rgba(218,165,32,0.15)] dark:hover:border-white/20 snap-center rtl flex flex-col justify-between">
      <div>
        {/* Header: User Info & Date */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            {userPhoto ? (
              <img
                src={userPhoto}
                alt={userName}
                className="w-12 h-12 rounded-full object-cover border-2 border-amber-400"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white font-bold text-lg border-2 border-amber-400">
                {userName?.charAt(0) || 'U'}
              </div>
            )}
            <div>
              <h4 className="font-bold text-gray-900 dark:text-white text-md line-clamp-1">
                {userName}
                {currentUser?.uid === review.userId && (
                  <span className="mr-2 text-[10px] bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400 px-1.5 py-0.5 rounded-full uppercase">أنت</span>
                )}
              </h4>
              <span className="text-md font-medium text-gray-500 dark:text-gray-400">
                {dateStr}
              </span>
            </div>
          </div>

          {/* User's own review edit button */}
          {currentUser?.uid === review.userId && (
            <button 
              className="p-1.5 text-slate-400 hover:text-primary-500 transition-colors"
              onClick={() => {
                if (window.dispatchEvent) {
                  window.dispatchEvent(new CustomEvent('OPEN_REVIEW_EDIT', { 
                    detail: { targetType: review.targetType, targetId: review.targetId } 
                  }));
                }
              }}
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Rating Stars */}
        <div className="mb-4">
          <StarRating rating={rating} readOnly size={18} />
        </div>

        {/* Review Content */}
        <div 
          className="text-gray-700 dark:text-gray-300 font-medium leading-relaxed mb-4 italic prose prose-sm dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: reviewText ? DOMPurify.sanitize(reviewText) : "تقييم بدون تعليق." }}
        />

        {/* Reply Display */}
        {(review.reply || review.adminReply) && !isEditing && (
          <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-500/10 rounded-xl relative border border-amber-100 dark:border-amber-500/20 group">
            <div className="flex items-start justify-between">
              <span className="text-md font-extrabold text-amber-600 dark:text-amber-400 block mb-1">
                رد {review.reply?.instructorName || "فريق لُباب"}:
              </span>
              {canReply && (
                <button
                  onClick={() => {
                    setReplyText(review.reply?.text || review.adminReply);
                    setIsEditing(true);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 transition-opacity p-1"
                  title="تعديل الرد"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div 
              className="text-md text-slate-700 dark:text-zinc-300 leading-relaxed font-medium prose prose-sm dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(review.reply?.text || review.adminReply || '') }}
            />
          </div>
        )}
      </div>

      {/* Reply Action Form */}
      {canReply && (!review.reply && !review.adminReply || isEditing) && (
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-white/5">
          {!showReplyInput && !isEditing ? (
            <button
              onClick={() => setShowReplyInput(true)}
              className="flex items-center gap-2 text-primary-600 dark:text-primary-400 font-bold hover:underline"
            >
              <MessageCircle className="w-4 h-4" />
              أضف رداً على التقييم
            </button>
          ) : (
            <form onSubmit={handleReplySubmit} className="space-y-2">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="اكتب ردك هنا..."
                className="w-full p-3 rounded-xl bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 outline-none text-sm font-medium"
                rows={2}
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isReplying || !replyText.trim()}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all disabled:opacity-50"
                >
                  {isReplying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {isEditing ? 'تحديث الرد' : 'إرسال الرد'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowReplyInput(false);
                    setIsEditing(false);
                    setReplyText('');
                  }}
                  className="text-slate-500 text-sm font-bold px-4 py-2"
                >
                  إلغاء
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewCard;
